import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { LanguageService } from './language.service';
import { HttpApplicationStatsService } from './http/http-application-stats.service';
import { ApplicationStats } from '../../shared/models/entities/application-stats.entity';
import { ExamType } from '../../shared/models/entities/exam-type.entity';
import { forkJoin } from 'rxjs';
import { AuthorizedExamType } from '../../shared/models/enums/exam.enums';
import { PricingBlock, PricingSection } from '../../shared/models/interfaces/pricing.interface';
import { AppTypeService } from './app-type.service';

@Injectable({
  providedIn: 'root',
})
export class PricingService {
  applicationsStats: ApplicationStats;
  examTypes: ExamType[];

  freePricingBlocks: PricingBlock[];
  premiumPlusPricingBlocks: PricingBlock[];
  elitePricingBlocks: PricingBlock[];

  pricingSections: PricingSection[];

  constructor(
    private httpApplicationStatsService: HttpApplicationStatsService,
    private appTypeService: AppTypeService,
    private languageService: LanguageService,
    private translateService: TranslateService
  ) {
    forkJoin([
      this.httpApplicationStatsService.getApplicationStats(),
      this.httpApplicationStatsService.countPublishedExamsGroupedByType(),
    ]).subscribe(([stats, examTypes]) => {
      this.applicationsStats = new ApplicationStats(stats);
      this.examTypes = examTypes['hydra:member']
        .map((examType) => new ExamType(examType))
        .sort((a, b) => (a.priority > b.priority ? 1 : -1));

      this.initPricingBlocks();
      this.initPricingTableSections();
    });
  }

  initPricingBlocks() {
    if (this.languageService.isEcni()) {
      this.freePricingBlocks = [
        {
          title: this.appTypeService.isDefault
            ? this.translateService.instant('pricing.exams_a_laffiche_detail')
            : $localize`Examens à l'affiche`,
        },
        {
          title: this.translateService.instant('item.images'),
        },
      ];
    } else {
      this.freePricingBlocks = [
        {
          title: this.translateService.instant('pricing.exams_a_laffiche'),
        },
        {
          title: this.translateService.instant('item.images'),
        },
      ];
    }

    const conftubeType = this.examTypes.find(
      (examType) => examType.name === AuthorizedExamType.CONFTUBE
    );
    if (this.languageService.isEcni()) {
      const ecosType = this.examTypes.find((examType) => examType.name === AuthorizedExamType.ECOS);
      this.premiumPlusPricingBlocks = [
        {
          title: `${
            this.applicationsStats.totalPublishedExams -
            (ecosType ? ecosType.totalPublishedExams : 0)
          } ${this.translateService.instant('examens.examens')}`,
        },
        {
          title: `${ecosType ? ecosType.totalPublishedExams : 0} ECOS`,
        },
        {
          title: `${
            this.applicationsStats.totalPublishedOnTrainingQuestions
          } ${this.translateService.instant('pricing.q_trainings')}`,
        },
        {
          title: `${
            this.applicationsStats.totalPublishedFolderQuestions +
            this.applicationsStats.totalUnpublishedOnTrainingPublishedOnExamExamContentQuestions
          } ${$localize`Questions Exams`}`,
        },
        {
          title: this.translateService.instant('training.ancrage'),
        },
        {
          title: `${
            conftubeType ? conftubeType.totalPublishedExams : 0
          } ${this.translateService.instant('premium.videos')}`,
        },
        {
          title: $localize`${this.applicationsStats.totalEDNCasts} EDNCasts`,
        },
        {
          title: $localize`${this.applicationsStats.totalECOCasts} ECOCasts`,
        },
        {
          title: `${
            this.applicationsStats.totalPublishedOnLessonsCourses
          } ${this.translateService.instant('pricing.cours_premium')}`,
        },
      ];
    } else if (this.languageService.isPrepSSM()) {
      this.premiumPlusPricingBlocks = [
        {
          title: `${
            this.applicationsStats.totalPublishedExams -
            (conftubeType ? conftubeType.totalPublishedExams : 0)
          } ${this.translateService.instant('examens.examens')}`,
        },
        {
          title: `${
            this.applicationsStats.totalPublishedOnTrainingQuestions
          } ${this.translateService.instant('pricing.q_trainings')}`,
        },
        {
          title: this.translateService.instant('pricing.ancrage'),
        },
        {
          title: this.translateService.instant('premium.turbo'),
        },
        {
          title: this.translateService.instant('item.images'),
        },
      ];
    } else if (this.languageService.isPrepMIR()) {
      this.premiumPlusPricingBlocks = [
        {
          title: `${
            conftubeType ? conftubeType.totalPublishedExams : 0
          } ${this.translateService.instant('premium.videos')}`,
          strong: true,
          text: `+ ${
            (conftubeType ? conftubeType.totalPublishedExams : 0) * 5
          } ${this.translateService.instant('pricing.quiz_video')}`,
        },
        {
          title: `${
            this.applicationsStats.totalPublishedExams -
            (conftubeType ? conftubeType.totalPublishedExams : 0)
          } ${this.translateService.instant('examens.examens')}`,
        },
        {
          title: `${
            this.applicationsStats.totalPublishedOnTrainingQuestions
          } ${this.translateService.instant('pricing.q_trainings')}`,
        },
        {
          title: this.translateService.instant('pricing.ancrage'),
        },
        {
          title: this.translateService.instant('item.images'),
        },
      ];
    }

    if (this.languageService.isPrepSSM()) {
      this.elitePricingBlocks = [
        {
          title: `${
            conftubeType ? conftubeType.totalPublishedExams : 0
          } ${this.translateService.instant('premium.videos')}`,
          strong: true,
          text: `+ ${
            (conftubeType ? conftubeType.totalPublishedExams : 0) * 5
          } ${this.translateService.instant('pricing.quiz_video')}`,
        },
        {
          title: `${
            this.applicationsStats.totalPublishedExams -
            (conftubeType ? conftubeType.totalPublishedExams : 0)
          } ${this.translateService.instant('examens.examens')}`,
        },
        {
          title: `${
            this.applicationsStats.totalPublishedOnTrainingQuestions
          } ${this.translateService.instant('pricing.q_trainings')}`,
        },
        {
          title: this.translateService.instant('pricing.ancrage'),
        },
        {
          title: this.translateService.instant('premium.turbo'),
        },
        {
          title: this.translateService.instant('item.images'),
        },
      ];
    }
  }

  initPricingTableSections() {
    const conftubeType = this.examTypes.find(
      (examType) => examType.name === AuthorizedExamType.CONFTUBE
    );
    if (this.languageService.isEcni()) {
      const ecosType = this.examTypes.find((examType) => examType.name === AuthorizedExamType.ECOS);
      this.pricingSections = [
        {
          title: `${
            this.applicationsStats.totalPublishedOnTrainingQuestions
          } ${$localize`Questions training`}`,
          icon: 'icon-training',
          rows: [
            {
              title: this.translateService.instant('pricing.mode_ancrage'),
              availableInFree: false,
              availableInPremiumPlus: true,
            },
            {
              title: this.translateService.instant('pricing.mode_personnalise'),
              availableInFree: false,
              availableInPremiumPlus: true,
            },
          ],
        },
        {
          title: `${
            this.applicationsStats.totalPublishedFolderQuestions +
            this.applicationsStats.totalUnpublishedOnTrainingPublishedOnExamExamContentQuestions
          } ${$localize`Questions Examens`}`,
          icon: 'icon-exams',
          rows: [
            {
              title: this.translateService.instant('pricing.exam_affiche'),
              availableInFree: true,
              availableInPremiumPlus: true,
            },
            {
              title: `${
                this.applicationsStats.totalPublishedExams -
                (ecosType ? ecosType.totalPublishedExams : 0)
              } ${this.translateService.instant('examens.examens')}`,
              text: this.appTypeService.isDefault
                ? this.translateService.instant('pricing.list_exams_premium')
                : $localize`Annales Classées Corrigées, Examen Blanc Classant, Séance, Colle`,
              availableInFree: false,
              availableInPremiumPlus: true,
            },
            {
              title: this.translateService.instant('pricing.rejouer'),
              availableInFree: false,
              availableInPremiumPlus: true,
            },
            {
              title: this.translateService.instant('pricing.poser_question'),
              availableInFree: false,
              availableInPremiumPlus: true,
            },
          ],
        },
        ...(this.appTypeService.isDefault
          ? [
              {
                title: `${ecosType ? ecosType.totalPublishedExams : 0} ECOS`,
                icon: 'icon-ecos',
                showAvailable: true,
                availableInFree: false,
                availableInPremiumPlus: true,
                rows: [],
              },
            ]
          : []),
        {
          title: `${
            this.applicationsStats.totalPublishedOnLessonsCourses
          } ${this.translateService.instant('pricing.cours')}`,
          icon: 'icon-course',
          rows: [
            {
              title: this.translateService.instant('item.images'),
              availableInFree: true,
              availableInPremiumPlus: true,
            },
            {
              title: this.translateService.instant('pricing.consutables'),
              availableInFree: false,
              availableInPremiumPlus: true,
            },
            {
              title: this.translateService.instant('pricing.imprimable'),
              availableInFree: false,
              availableInPremiumPlus: true,
            },
          ],
        },
        ...(this.appTypeService.isDefault
          ? [
              {
                title: $localize`Play`,
                icon: 'icon-video-menu',
                rows: [
                  {
                    title: `${
                      conftubeType ? conftubeType.totalPublishedExams : 0
                    } ${this.translateService.instant('premium.videos')}`,
                    availableInFree: false,
                    availableInPremiumPlus: true,
                  },
                  {
                    title: $localize`${this.applicationsStats.totalEDNCasts} EDNCasts`,
                    availableInFree: false,
                    availableInPremiumPlus: true,
                  },
                  {
                    title: $localize`${this.applicationsStats.totalECOCasts} ECOCasts`,
                    availableInFree: false,
                    availableInPremiumPlus: true,
                  },
                ],
              },
            ]
          : []),
        {
          title: `${this.translateService.instant('challenge.challenge')}`,
          icon: 'icon-challenge',
          showAvailable: true,
          availableInFree: false,
          availableInPremiumPlus: true,
          rows: [],
        },
      ];
    } else if (this.languageService.isPrepSSM()) {
      this.pricingSections = [
        {
          title: `${
            this.applicationsStats.totalPublishedOnTrainingQuestions
          } ${this.translateService.instant('sidebar.training')}`,
          icon: 'icon-training',
          rows: [
            {
              title: this.translateService.instant('pricing.mode_ancrage'),
              availableInFree: false,
              availableInPremiumPlus: true,
              availableInElite: true,
            },
            {
              title: this.translateService.instant('pricing.mode_personnalise'),
              availableInFree: false,
              availableInPremiumPlus: true,
              availableInElite: true,
            },
          ],
        },
        {
          title: `${
            conftubeType ? conftubeType.totalPublishedExams : 0
          } ${this.translateService.instant('premium.videos')}`,
          icon: 'icon-video-menu',
          showAvailable: true,
          availableInFree: false,
          availableInPremiumPlus: false,
          availableInElite: true,
          rows: [],
        },
        {
          title: `${this.translateService.instant('premium.turbo')}`,
          icon: 'icon-turbo',
          rows: [
            {
              title: this.translateService.instant('premium.turbo_text'),
              availableInFree: false,
              availableInPremiumPlus: true,
              availableInElite: true,
            },
          ],
        },
        {
          title: `${
            this.applicationsStats.totalPublishedExams -
            (conftubeType ? conftubeType.totalPublishedExams : 0)
          } ${this.translateService.instant('examens.examens')}`,
          icon: 'icon-exams',
          rows: [
            {
              title: this.translateService.instant('pricing.exam_affiche'),
              availableInFree: true,
              availableInPremiumPlus: true,
              availableInElite: true,
            },
            {
              title: this.translateService.instant('pricing.tous_examens'),
              text: this.translateService.instant('pricing.list_exams_premium'),
              availableInFree: false,
              availableInPremiumPlus: true,
              availableInElite: true,
            },
            {
              title: this.translateService.instant('pricing.rejouer'),
              availableInFree: false,
              availableInPremiumPlus: true,
              availableInElite: true,
            },
            {
              title: this.translateService.instant('pricing.poser_question'),
              availableInFree: false,
              availableInPremiumPlus: true,
              availableInElite: true,
            },
          ],
        },
        {
          title: `${this.translateService.instant('pricing.cours')}`,
          icon: 'icon-course',
          rows: [
            {
              title: this.translateService.instant('item.images'),
              availableInFree: true,
              availableInPremiumPlus: true,
              availableInElite: true,
            },
          ],
        },
        {
          title: `${this.translateService.instant('challenge.challenge')}`,
          icon: 'icon-challenge',
          showAvailable: true,
          availableInFree: false,
          availableInPremiumPlus: true,
          availableInElite: true,
          rows: [],
        },
      ];
    } else if (this.languageService.isPrepMIR()) {
      this.pricingSections = [
        {
          title: `${
            this.applicationsStats.totalPublishedOnTrainingQuestions
          } ${this.translateService.instant('sidebar.training')}`,
          icon: 'icon-training',
          rows: [
            {
              title: this.translateService.instant('pricing.mode_ancrage'),
              availableInFree: false,
              availableInPremiumPlus: true,
            },
            {
              title: this.translateService.instant('pricing.mode_personnalise'),
              availableInFree: false,
              availableInPremiumPlus: true,
            },
          ],
        },
        {
          title: `${
            conftubeType ? conftubeType.totalPublishedExams : 0
          } ${this.translateService.instant('premium.videos')}`,
          icon: 'icon-video-menu',
          showAvailable: true,
          availableInFree: false,
          availableInPremiumPlus: true,
          rows: [],
        },
        {
          title: `${
            this.applicationsStats.totalPublishedExams -
            (conftubeType ? conftubeType.totalPublishedExams : 0)
          } ${this.translateService.instant('examens.examens')}`,
          icon: 'icon-exams',
          rows: [
            {
              title: this.translateService.instant('pricing.exam_affiche'),
              availableInFree: true,
              availableInPremiumPlus: true,
            },
            {
              title: this.translateService.instant('pricing.tous_examens'),
              text: this.translateService.instant('pricing.list_exams_premium'),
              availableInFree: false,
              availableInPremiumPlus: true,
            },
            {
              title: this.translateService.instant('pricing.rejouer'),
              availableInFree: false,
              availableInPremiumPlus: true,
            },
            {
              title: this.translateService.instant('pricing.poser_question'),
              availableInFree: false,
              availableInPremiumPlus: true,
            },
          ],
        },
        {
          title: `${this.translateService.instant('pricing.cours')}`,
          icon: 'icon-course',
          rows: [
            {
              title: this.translateService.instant('item.images'),
              availableInFree: true,
              availableInPremiumPlus: true,
            },
          ],
        },
        {
          title: `${this.translateService.instant('challenge.challenge')}`,
          icon: 'icon-challenge',
          showAvailable: true,
          availableInFree: false,
          availableInPremiumPlus: true,
          rows: [],
        },
      ];
    }
  }
}
